import React, { useState, useEffect } from 'react';
import Accordion from './Accordion';

// Add type definitions
type ActionType = 'CREATE' | 'UPDATE' | 'DELETE';
type EntityType = 'Job Details' | 'Job Address' | 'Jobsite Delete' | 'Comments' | 'Job Status' | 'Jobsite Visit Details' | 'Jobsite Visit Trades' | 'Jobsite Visit Files' | 'Jobsite Visit Comments';

interface LogsFilterOverlayProps {
    isOpen: boolean;
    onClose: () => void;
    onClearAll?: () => void;
    activeFilters?: {
        startDate: string;
        endDate: string;
        actionType: ActionType[];
        entityType: EntityType[];
        search: string;
    };
    onApply: (filters: {
        startDate: string;
        endDate: string;
        actionType: ActionType[];
        entityType: EntityType[];
        search: string;
    }) => void;
}

const LogsFilterOverlay: React.FC<LogsFilterOverlayProps> = ({
    isOpen,
    onClose,
    onClearAll,
    activeFilters = {
        startDate: '',
        endDate: '',
        actionType: [],
        entityType: [],
        search: ''
    },
    onApply,
}) => {
    // Add state for expanded sections
    const [expandedSections, setExpandedSections] = useState({
        dateRange: true,
        actionType: false,
        entityType: false,
    });

    // Add state for filters with proper typing
    const [filters, setFilters] = useState<{
        startDate: string;
        endDate: string;
        actionType: ActionType[];
        entityType: EntityType[];
    }>({
        startDate: activeFilters.startDate || '',
        endDate: activeFilters.endDate || '',
        actionType: activeFilters.actionType || [],
        entityType: activeFilters.entityType || [],
    });

    // Effect to update local filters when activeFilters change
    useEffect(() => {
        if (isOpen) {
            setFilters({
                startDate: activeFilters.startDate || '',
                endDate: activeFilters.endDate || '',
                actionType: activeFilters.actionType || [],
                entityType: activeFilters.entityType || [],
            });
        }
    }, [activeFilters, isOpen]);

    // Action type options
    const actionTypes: Array<{ value: ActionType; label: string; byLabel: string }> = [
        { value: 'CREATE', label: 'Create', byLabel: 'Created by' },
        { value: 'UPDATE', label: 'Update', byLabel: 'Updated by' },
        { value: 'DELETE', label: 'Delete', byLabel: 'Deleted by' }
    ];

    // Entity type options
    const entityTypes: Array<{ value: EntityType; label: string }> = [
        { value: 'Job Details', label: 'Job Details' },
        { value: 'Job Address', label: 'Job Address' },
        { value: 'Jobsite Delete', label: 'Jobsite Delete' },
        { value: 'Comments', label: 'Comments' },
        { value: 'Job Status', label: 'Job Status' },
        { value: 'Jobsite Visit Details', label: 'Visit Details' },
        { value: 'Jobsite Visit Trades', label: 'Visit Trades' },
        { value: 'Jobsite Visit Files', label: 'Visit Files' },
        { value: 'Jobsite Visit Comments', label: 'Visit Comments' },
    ];

    const toggleSection = (section: keyof typeof expandedSections) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Handle filter changes
    const handleFilterChange = (field: keyof typeof filters, value: any) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            startDate: '',
            endDate: '',
            actionType: [],
            entityType: [],
        });
    };

    // Handle clear all - clear local state and immediately apply changes
    const handleClearAll = () => {
        resetFilters();
        // Apply the cleared filters immediately
        onApply({
            startDate: '',
            endDate: '',
            actionType: [],
            entityType: [],
            search: activeFilters.search || ''
        });
        onClose(); // Close the overlay after clearing
    };

    // Handle apply filters
    const handleApply = () => {
        onApply({
            ...filters,
            search: activeFilters.search || ''
        });
    };

    return (
        <div 
            data-testid="logs-filter-overlay"
            className={`fixed inset-0 bg-black transition-all duration-300 ease-in-out ${
                isOpen ? 'bg-opacity-50' : 'bg-opacity-0 pointer-events-none'
            } z-50`}
            onClick={e => {
                // Only close if the click is on the overlay itself, not the side panel
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
            id="logs-filter-overlay"
        >
            <div 
                className={`fixed right-0 bg-white h-screen  w-full md:w-[500px] shadow-lg transition-all duration-300 ease-in-out ${
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-xl font-semibold">Filter by</h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full"
                        id="close-logs-filter"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="p-4 overflow-y-auto h-[calc(100vh-180px)]">
                    <div className="space-y-4">
                        {/* Date Range Section */}
                        <Accordion 
                            title="Date Range" 
                            isExpanded={expandedSections.dateRange}
                            onToggle={() => toggleSection('dateRange')}
                        >
                            <div className="space-y-3 mt-3">
                                <div>
                                    <label htmlFor="logs-filter-start-date" className="block text-sm text-gray-600 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        id="logs-filter-start-date"
                                        value={filters.startDate ? filters.startDate.split('T')[0] : ''}
                                        max={filters.endDate ? filters.endDate.split('T')[0] : undefined}
                                        onChange={(e) => handleFilterChange('startDate', e.target.value ? new Date(e.target.value).toISOString() : '')}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="logs-filter-end-date" className="block text-sm text-gray-600 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        id="logs-filter-end-date"
                                        value={filters.endDate ? filters.endDate.split('T')[0] : ''}
                                        min={filters.startDate ? filters.startDate.split('T')[0] : undefined}
                                        onChange={(e) => handleFilterChange('endDate', e.target.value ? new Date(e.target.value).toISOString() : '')}
                                    />
                                </div>
                            </div>
                        </Accordion>

                        {/* Action Type Section */}
                        <Accordion 
                            title="Action Type" 
                            isExpanded={expandedSections.actionType}
                            onToggle={() => toggleSection('actionType')}
                        >
                            <div className="space-y-2 mt-2">
                                {actionTypes.map((type) => (
                                    <label key={type.value} className="flex items-center gap-2" htmlFor={`logs-filter-action-${type.value.toLowerCase()}`}>
                                        <input
                                            type="checkbox"
                                            className="custom-checkbox" 
                                            id={`logs-filter-action-${type.value.toLowerCase()}`}
                                            checked={filters.actionType.includes(type.value)}
                                            onChange={(e) => {
                                                const newActionTypes = e.target.checked
                                                    ? [...filters.actionType, type.value]
                                                    : filters.actionType.filter(t => t !== type.value);
                                                handleFilterChange('actionType', newActionTypes);
                                            }}
                                        />
                                        <span>{type.label}</span>
                                        <span className="text-sm text-gray-500">({type.byLabel})</span>
                                    </label>
                                ))}
                            </div>
                        </Accordion>

                        {/* Entity Type Section */}
                        <Accordion 
                            title="Entity Type" 
                            isExpanded={expandedSections.entityType}
                            onToggle={() => toggleSection('entityType')}
                        >
                            <div className="space-y-2 mt-2">
                                {entityTypes.map((type) => (
                                    <label key={type.value} className="flex items-center gap-2" htmlFor={`logs-filter-entity-${type.value.toLowerCase().replace(/\s+/g, '-')}`}>
                                        <input
                                            type="checkbox"
                                            className="custom-checkbox" 
                                            id={`logs-filter-entity-${type.value.toLowerCase().replace(/\s+/g, '-')}`}
                                            checked={filters.entityType.includes(type.value)}
                                            onChange={(e) => {
                                                const newEntityTypes = e.target.checked
                                                    ? [...filters.entityType, type.value]
                                                    : filters.entityType.filter(t => t !== type.value);
                                                handleFilterChange('entityType', newEntityTypes);
                                            }}
                                        />
                                        <span>{type.label}</span>
                                    </label>
                                ))}
                            </div>
                        </Accordion>
                    </div>
                </div>

                {/* Fixed Bottom Buttons */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t">
                    <div className="flex items-center justify-end gap-3">
                        <button
                            className="btn-secondary"
                            onClick={handleClearAll}
                            id="logs-filter-clear-all"
                        >
                            Clear All
                        </button>
                        <button 
                            className="btn-primary"
                            onClick={handleApply}
                            id="logs-filter-apply"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LogsFilterOverlay; 